/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {MarkType} from 'prosemirror-model';
import {EditorState, Transaction} from 'prosemirror-state';
import {Command, toggleMark} from 'prosemirror-commands';
import {applyMark} from '../../../commands';
import {isMarkActive} from '../../../helpers';
import isImageSelected from '../../../utils/isImageSelected';

class Mark {
    /**
     * Mark name.
     */
    name: string;

    /**
     * Default constructor.
     * 
     * @param name Name.
     */
    constructor(name: string) {
        this.name = name;
    }

    /**
     * Apply function.
     * 
     * @returns Command.
     */
    apply(): Command {
        return (state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {

            const {schema} = state;

            const type: MarkType = schema.marks[this.name];
            if (!type) {
                return false;
            }

            return applyMark(type)(state, dispatch);
        };
    }

    /**
     * Toggle command.
     * 
     * @returns Command.
     */
    toggle(): Command {
        return (state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {

            const {schema} = state;

            const type: MarkType = schema.marks[this.name];
            if (!type) {
                return false;
            }

            return toggleMark(type)(state, dispatch);
        };
    }

    /**
     * Is command active.
     * 
     * @param state EditorState.
     * @returns Boolean.
     */
    isActive(state: EditorState): boolean {
        const {schema} = state;

        const type: MarkType = schema.marks[this.name];

        if (!type) {
            return false;
        }

        return isMarkActive(state, type);
    }

    /**
     * Does command can execute.
     * 
     * @param state EditorState.
     * @returns Boolean.
     */
    canExecute(state: EditorState): boolean {
        return !isImageSelected()(state) && this.toggle()(state);
    }
}

export default Mark;
